import * as yup from "yup";
import { useCurrentAccountQuery } from "../api/queries/useAccountQuery";
import { useClientCreditCardsQuery } from "../api/queries/useClientCreditCardsQuery";
import { useClientQuery } from "../api/queries/useClientQuery";
import { useStripePaymentMethodsQuery } from "../api/stripe/useStripePaymentMethods";
import { USER_PAYMENT_SYSTEMS } from "../consts/api";
import { useAppTranslation } from "../i18n/useAppTranslation";
import { uiNotification } from "../services/UINotificationService";

var schema = yup.array().of(
  yup.object({
    id: yup.string().required(),
    cardNumber: yup.string().required(),
    billingZip: yup.string().nullable(),
    isPrimary: yup.boolean().required(),
    extra: yup
      .object({
        dbId: yup.mixed().nullable(),
      })
      .required(),
  }),
);

var clearentCardsAdapter = (cards = []) =>
  cards.map((card) => ({
    id: card.cardId || "",
    cardNumber: card.cardNumber || "",
    billingZip: card.billingZip || null,
    isPrimary: card.isPrimary,
    extra: {
      dbId: card.id || null,
    },
  }));

var stripeCardsAdapter = (cards = []) =>
  cards.map((card) => ({
    id: card.id || "",
    cardNumber: card.cardNumber || "",
    billingZip: null,
    isPrimary: card.is_primary,
    extra: {
      dbId: null,
    },
  }));

export function usePatientPaymentMethods({ patientId }) {
  const { tCommon } = useAppTranslation.Common();
  const accountQuery = useCurrentAccountQuery();
  const paymentSystem = accountQuery.data?.paymentSystem;

  const clientQuery = useClientQuery(
    {
      clientId: Number(patientId),
    },
    {
      enabled: !!patientId,
      onError: () => {
        uiNotification.error(tCommon("error.fetchPatient"));
      },
    },
  );

  const isClearent =
    accountQuery.isSuccess &&
    paymentSystem === USER_PAYMENT_SYSTEMS.clearent &&
    !!patientId;

  const isStripe =
    accountQuery.isSuccess &&
    paymentSystem === USER_PAYMENT_SYSTEMS.stripe &&
    !!patientId;

  const clearentCardsQuery = useClientCreditCardsQuery(
    {
      clientId: Number(patientId),
    },
    {
      enabled: isClearent,
      onError: () => {
        uiNotification.error(tCommon("error.fetchPatientCreditCards"));
      },
    },
  );

  const stripeCardsQuery = useStripePaymentMethodsQuery(
    { patientId },
    {
      enabled: isStripe,
      onError: () => {
        uiNotification.error(tCommon("error.fetchPatientCreditCards"));
      },
    },
  );

  return {
    paymentSystem,

    isLoading:
      accountQuery.isLoading ||
      clientQuery.isLoading ||
      (isClearent ? clearentCardsQuery.isLoading : false) ||
      (isStripe ? stripeCardsQuery.isLoading : false),

    isPosEnabled: accountQuery.data?.isPosEnabled || false,

    data: schema.validateSync(
      paymentSystem === USER_PAYMENT_SYSTEMS.stripe
        ? stripeCardsAdapter(stripeCardsQuery.data)
        : clearentCardsAdapter(clearentCardsQuery.data?.creditCards),
      { strict: true },
    ),

    canAdd: clientQuery.data?.canAddCreditCard || false,

    clearent:
      paymentSystem === USER_PAYMENT_SYSTEMS.clearent
        ? {
            publicKey: clearentCardsQuery.data?.clinicClearentPublicKey || null,
            billingZip: clientQuery.data?.pinCode || null,
            billingEmail: clientQuery.data?.emails?.[0] || null,
          }
        : null,

    refetch:
      paymentSystem === USER_PAYMENT_SYSTEMS.stripe
        ? stripeCardsQuery.refetch
        : clearentCardsQuery.refetch,
  };
}
